import { SocialLogin } from '@capgo/capacitor-social-login';

/**
 * Sign in with Apple nativo (iOS) via @capgo/capacitor-social-login.
 *
 * Obrigatório na App Store (Guideline 4.8) porque o app oferece login Google.
 * Só faz sentido no iOS — no Android/PWA o botão nem aparece (ver Login.tsx).
 *
 * Fluxo: o plugin usa o ASAuthorization nativo (entitlement
 * com.apple.developer.applesignin no build iOS) e devolve um `identityToken`
 * (JWT da Apple) cujo `aud` é o bundle id `br.app.bow`. Enviamos esse token pro
 * `POST /auth/apple`, que valida contra o JWKS da Apple. O NOME só vem no
 * PRIMEIRO login (Apple não reenvia depois) — mandamos junto quando presente.
 */

let initPromise: Promise<void> | null = null;

function ensureInit(): Promise<void> {
  if (!initPromise) {
    // No iOS nativo o Apple Sign-In não precisa de clientId (usa o bundle id
    // via entitlement). clientId/redirectUrl só seriam necessários num fluxo
    // web/Android com Service ID, que não usamos.
    initPromise = SocialLogin.initialize({ apple: {} }).catch((err) => {
      initPromise = null;
      throw err;
    });
  }
  return initPromise;
}

export interface AppleSignInResult {
  idToken: string;
  /** Nome montado a partir de givenName/familyName — só no 1º login. */
  name: string | null;
}

/** Dispara o Sign in with Apple nativo e retorna o identityToken (+ nome). */
export async function nativeAppleSignIn(): Promise<AppleSignInResult> {
  await ensureInit();
  const res = await SocialLogin.login({
    provider: 'apple',
    options: { scopes: ['email', 'name'] }
  });
  const result = res.result as {
    idToken?: string | null;
    profile?: { givenName?: string | null; familyName?: string | null } | null;
  };
  if (!result?.idToken) {
    throw new Error('apple_no_id_token');
  }
  const name =
    [result.profile?.givenName, result.profile?.familyName]
      .filter(Boolean)
      .join(' ')
      .trim() || null;
  return { idToken: result.idToken, name };
}
